import { DataService } from './data-service';
import { safeWrite } from './safe-write';

/**
 * One-time migration: move quote statuses from localStorage to project records.
 *
 * MIGRATION-FLAG LATCHING FIX (v5.12.0+): the previous implementation
 * set the `rjbird_quote_statuses_migrated` flag BEFORE the backend
 * writes had actually completed. Under the v5.12.0 API-only contract
 * a write against a stopped Docker throws; so first-boot-during-outage
 * would latch the "migrated" flag even though zero rows reached
 * Postgres — producing a permanent broken state (migration never
 * retries next session). Fix: await all updates via safeWrite, and
 * only set the flag if EVERY write succeeded. A single failure
 * leaves the flag unset so the migration can retry.
 */
export async function migrateLegacyQuoteStatuses(projects: any[]): Promise<any[]> {
    try {
        const migrated = localStorage.getItem('rjbird_quote_statuses_migrated');
        if (!migrated) {
            const stored = JSON.parse(localStorage.getItem('rjbird_quote_statuses') || '{}');
            const entries = Object.entries(stored);
            if (entries.length > 0) {
                const writes: Array<Promise<{ ok: boolean; error?: any }>> = [];
                const updated = projects.map(proj => {
                    const ls = (stored as Record<string, string>)[proj.id];
                    if (ls && !proj.quoteStatus) {
                        writes.push(
                            safeWrite(
                                DataService.projects.updateQuoteStatus(proj.id, ls),
                                { what: 'projects.updateQuoteStatus', context: { projectId: proj.id, flow: 'rjbird_quote_statuses migration' } },
                            ),
                        );
                        return { ...proj, quoteStatus: ls };
                    }
                    return proj;
                });
                const results = await Promise.all(writes);
                const allOk = results.every(r => r.ok);
                if (allOk) {
                    localStorage.setItem('rjbird_quote_statuses_migrated', '1');
                } else {
                    console.warn('[QM] rjbird_quote_statuses migration had failures — flag not latched, will retry next session.');
                }
                return updated;
            } else {
                localStorage.setItem('rjbird_quote_statuses_migrated', '1');
                return projects;
            }
        }
        return projects;
    } catch {
        return projects;
    }
}
